import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";
import { MAX_SIZE_MB } from "../../helper";

const customModalStyles = {
  content: {
    padding: "20px",
    width: "80%",
    maxWidth: "640px",
    margin: "auto",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    maxHeight: "90vh",
    overflowY: "auto",
  },
};

const fieldStyle = {
  width: "100%",
  padding: "8px",
  fontSize: "14px",
  borderRadius: "4px",
  border: "1px solid #ccc",
};

const labelStyle = {
  display: "block",
  fontWeight: "bold",
  marginBottom: "5px",
};

const emptyForm = {
  title: "",
  description: "",
  sdcProgramId: "",
  courseCode: "",
  courseDuration: "",
  courseFee: "",
  courseDetails: "",
  admissionStart: "",
  admissionEnd: "",
  admissionOpen: false,
  eligibility: "",
  formLink: "",
};

const SdcCourses = () => {
  const [courses, setCourses] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [programFilter, setProgramFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imgFile, setImgFile] = useState(null);

  useEffect(() => {
    fetchPrograms();
    fetchCourses();
  }, []);

  const fetchPrograms = async () => {
    try {
      const res = await axiosInstance.get("sdc/programs/all");
      setPrograms(res.data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("program/all", {
        params: { sdcOnly: true },
      });
      setCourses(res.data?.data || []);
    } catch (error) {
      Swal.fire("Error", "Failed to load courses", "error");
    }
    setLoading(false);
  };

  const filtered = useMemo(() => {
    if (programFilter === "all") return courses;
    return courses.filter((c) => c.sdcProgramId === programFilter);
  }, [courses, programFilter]);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      sdcProgramId: programFilter !== "all" ? programFilter : "",
    });
    setImgFile(null);
    setShowModal(true);
  };

  const openEdit = async (id) => {
    const course = courses.find((c) => c._id === id);
    if (!course) return;
    setEditingId(id);
    setForm({
      title: course.title || "",
      description: course.description || "",
      sdcProgramId: course.sdcProgramId || "",
      courseCode: course.courseCode || "",
      courseDuration: course.courseDuration || "",
      courseFee: course.courseFee || "",
      courseDetails: course.courseDetails || "",
      admissionStart: course.admissionStart || "",
      admissionEnd: course.admissionEnd || "",
      admissionOpen: !!course.admissionOpen,
      eligibility: course.eligibility || "",
      formLink: course.formLink || "",
    });
    setImgFile(null);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      Swal.fire("Missing title", "Course title is required", "warning");
      return;
    }
    if (!form.sdcProgramId) {
      Swal.fire("Missing program", "Select an SDC program", "warning");
      return;
    }
    if (imgFile && imgFile.size > MAX_SIZE_MB * 1024 * 1024) {
      Swal.fire("File too large", `Max image size is ${MAX_SIZE_MB}MB`, "warning");
      return;
    }

    setSaving(true);
    try {
      const body = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        body.append(key, typeof value === "boolean" ? String(value) : value);
      });
      if (imgFile) body.append("img", imgFile);

      if (editingId) {
        body.append("_method", "PUT");
        await axiosInstance.post(`program/change/${editingId}`, body);
        Swal.fire("Saved", "Course updated", "success");
      } else {
        await axiosInstance.post("program/create", body);
        Swal.fire("Saved", "Course created", "success");
      }
      setShowModal(false);
      fetchCourses();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to save course",
        "error"
      );
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete this course?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`program/delete/${id}`);
      fetchCourses();
      Swal.fire("Deleted", "Course removed", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete course",
        "error"
      );
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>SDC Courses</h2>
          <p style={{ margin: "6px 0 0", color: "#666" }}>
            Courses under each program show on the NAVTTC Courses/Trainings
            page for that batch.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          style={{
            padding: "10px 20px",
            backgroundColor: "#4CAF50",
            color: "#fff",
            border: "none",
            borderRadius: 5,
            cursor: "pointer",
          }}
        >
          Add Course
        </button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <select
          value={programFilter}
          onChange={(e) => setProgramFilter(e.target.value)}
          style={{ ...fieldStyle, maxWidth: 360 }}
        >
          <option value="all">All programs</option>
          {programs.map((p) => (
            <option key={p._id} value={p._id}>
              {p.name}
              {p.batch?.name ? ` (${p.batch.name})` : ""}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <CircularProgress />
        </div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
          {filtered.length ? (
            filtered.map((course) => (
              <div
                key={course._id}
                style={{
                  width: 280,
                  padding: 16,
                  borderRadius: 8,
                  background: "#fff",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              >
                <h3 style={{ marginTop: 0 }}>{course.title}</h3>
                <p style={{ fontSize: 13, color: "#666", margin: "4px 0" }}>
                  Program: {course.sdcProgram?.name || "—"}
                </p>
                <p style={{ fontSize: 13, color: "#666", margin: "4px 0" }}>
                  Batch: {course.sdcProgram?.batch?.name || course.type || "—"}
                </p>
                {course.courseDuration && (
                  <p style={{ fontSize: 13, color: "#666", margin: "4px 0" }}>
                    Duration: {course.courseDuration}
                  </p>
                )}
                <Actions
                  courseId={course._id}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </div>
            ))
          ) : (
            <p>
              No SDC courses yet. Create a batch and program, then add courses
              here.
            </p>
          )}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        style={customModalStyles}
        contentLabel="Course"
      >
        <h2>{editingId ? "Edit Course" : "Add Course"}</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Program</label>
            <select
              name="sdcProgramId"
              style={fieldStyle}
              value={form.sdcProgramId}
              onChange={handleChange}
              required
            >
              <option value="" disabled>
                Select program
              </option>
              {programs.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name}
                  {p.batch?.name ? ` — ${p.batch.name}` : ""}
                </option>
              ))}
            </select>
          </div>
          {[
            { label: "Title", name: "title", required: true },
            { label: "Description", name: "description", type: "textarea" },
            { label: "Course Code", name: "courseCode" },
            { label: "Course Duration", name: "courseDuration" },
            { label: "Course Fee", name: "courseFee" },
            { label: "Course Details", name: "courseDetails", type: "textarea" },
            { label: "Eligibility", name: "eligibility" },
            { label: "Form Link", name: "formLink" },
            { label: "Admission Start", name: "admissionStart" },
            { label: "Admission End", name: "admissionEnd" },
          ].map(({ label, name, type, required }) => (
            <div key={name} style={{ marginBottom: 10 }}>
              <label style={labelStyle}>{label}</label>
              {type === "textarea" ? (
                <textarea
                  name={name}
                  style={{ ...fieldStyle, minHeight: 70 }}
                  value={form[name]}
                  onChange={handleChange}
                  required={required}
                />
              ) : (
                <input
                  name={name}
                  style={fieldStyle}
                  value={form[name]}
                  onChange={handleChange}
                  required={required}
                />
              )}
            </div>
          ))}
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImgFile(e.target.files?.[0] || null)}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                name="admissionOpen"
                checked={form.admissionOpen}
                onChange={handleChange}
              />
              Admission open
            </label>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "10px 20px",
                backgroundColor: "#4CAF50",
                color: "#fff",
                border: "none",
                borderRadius: 5,
                cursor: "pointer",
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              style={{
                padding: "10px 20px",
                backgroundColor: "#ccc",
                border: "none",
                borderRadius: 5,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SdcCourses;
